import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { AdeLoadingScreen } from "@/components/AdeLoadingSpinner";
import PrivacyConsentPrompt from "@/components/PrivacyConsentPrompt";
import { LevelUpCelebration } from "@/components/LevelUpCelebration";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { useRouteRobots } from "@/components/Seo";
import { t } from "@/i18n";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CreatorTerms from "@/pages/CreatorTerms";
import GoldenRingTerms from "@/pages/GoldenRingTerms";
import { CookieBanner } from "@/components/CookieBanner";
import { EdgeSwipeBack } from "@/components/EdgeSwipeBack";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import Nearby from "@/pages/Nearby";
import Connections from "@/pages/Connections";
import Requests from "@/pages/Requests";
import Here from "@/pages/Here";
import Alerts from "@/pages/Alerts";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import ProfessionalPublic from "@/pages/ProfessionalPublic";
import ProfessionalsSearch from "@/pages/ProfessionalsSearch";
import ProfessionalSettings from "@/pages/ProfessionalSettings";
import ProfessionalAnalytics from "@/pages/ProfessionalAnalytics";
import ProfessionalAudience from "@/pages/ProfessionalAudience";
import Chat from "@/pages/Chat";
import Messages from "@/pages/Messages";
import { CallManager } from "@/components/CallManager";
import Settings from "@/pages/Settings";

// Lazy-loaded routes (code splitting for faster initial load)
const DiscussionPage = lazy(() => import("@/pages/DiscussionPage"));
const MissionDetail = lazy(() => import("@/pages/MissionDetail"));
const ActiveMission = lazy(() => import("@/pages/ActiveMission"));
const MissionsHistory = lazy(() => import("@/pages/MissionsHistory"));
const MerchantMissions = lazy(() => import("@/pages/MerchantMissions"));
const MerchantMissionCreate = lazy(() => import("@/pages/MerchantMissionCreate"));
const CreatorRegistration = lazy(() => import("@/pages/CreatorRegistration"));
const CreatorKycPage = lazy(() => import("@/pages/CreatorKycPage"));
const CreatorWallet = lazy(() => import("@/pages/CreatorWallet"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const CallHistory = lazy(() => import("@/pages/CallHistory"));
const CreditsPage = lazy(() => import("@/pages/CreditsPage"));
const GiftCenter = lazy(() => import("@/pages/GiftCenter"));
const GoldenRingPage = lazy(() => import("@/pages/GoldenRingPage"));
const CreatorDashboardPage = lazy(() => import("@/pages/CreatorDashboardPage"));
const CreatorProgress = lazy(() => import("@/pages/CreatorProgress"));
const ExplorePage = lazy(() => import("@/pages/ExplorePage"));
const MentionRedirect = lazy(() => import("@/pages/MentionRedirect"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const PublicPost = lazy(() => import("@/pages/PublicPost"));
const PurchaseSuccessPage = lazy(() => import("@/components/CoinShop").then(m => ({ default: m.PurchaseSuccess })));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Suspended account: read-only mode — show a throttled notice on blocked write actions
let lastSuspendedToast = 0;
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && typeof detail === "string" && detail.startsWith("Account sospeso")) {
      const now = Date.now();
      if (now - lastSuspendedToast > 4000) {
        lastSuspendedToast = now;
        import("sonner").then(({ toast }) =>
          toast.error("Account sospeso: puoi solo visualizzare i contenuti"));
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = ({ children }) => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return children;
};

// Auth Callback - handles Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/login', { replace: true });
        return;
      }
      
      try {
        const response = await api.post('/auth/session', { session_id: sessionId });
        const { session_token, onboarding_completed, ...userData } = response.data;
        
        // Store token
        localStorage.setItem('token', session_token);
        
        // Store token in Service Worker for background sync
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'STORE_TOKEN',
            token: session_token
          });
        }
        
        // Clear hash and redirect
        window.history.replaceState(null, '', window.location.pathname);
        
        if (onboarding_completed) {
          navigate('/app/home', { replace: true, state: { user: userData } });
        } else {
          navigate('/onboarding', { replace: true, state: { user: userData } });
        }
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login', { replace: true });
      }
    };
    
    processAuth();
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pulse text-gray-400">{t('loginInProgress')}</div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [needsPrivacyConsent, setNeedsPrivacyConsent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { applyServerPreference } = useTheme();
  
  const checkPrivacyConsents = (userData) => {
    const consents = userData?.privacy_consents || {};
    return (
      consents.terms_accepted === true &&
      consents.privacy_accepted === true &&
      consents.location_consent === true
    );
  };
  
  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      applyServerPreference(location.state.user?.settings?.theme_preference);
      // Check privacy consents for OAuth users too
      if (!checkPrivacyConsents(location.state.user)) {
        setNeedsPrivacyConsent(true);
      }
      return;
    }
    
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }
      
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
        applyServerPreference(response.data?.settings?.theme_preference);
        
        // Check email verification FIRST
        if (response.data.email_verified === false && !location.pathname.includes('verify-email')) {
          navigate('/verify-email', { replace: true });
          return;
        }
        
        // Check privacy consents FIRST
        if (!checkPrivacyConsents(response.data)) {
          setNeedsPrivacyConsent(true);
          return; // Don't redirect to onboarding if privacy consents needed
        }
        
        // Check onboarding (only if privacy consents are OK)
        if (!response.data.onboarding_completed && !location.pathname.includes('onboarding')) {
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state, navigate, location.pathname]);
  
  const handleConsentsAccepted = async () => {
    // Force-set local state immediately to prevent loop
    setNeedsPrivacyConsent(false);
    
    // Then refresh user data in background
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      
      // Now check onboarding
      if (!response.data.onboarding_completed && !location.pathname.includes('onboarding')) {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };
  
  if (isAuthenticated === null) {
    return <AdeLoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Show privacy consent prompt if needed
  if (needsPrivacyConsent) {
    return <PrivacyConsentPrompt user={user} onConsentsAccepted={handleConsentsAccepted} />;
  }
  
  return <>{children}<LevelUpCelebration /></>;
};

// Public Route - redirect to app if authenticated
const PublicRoute = ({ children }) => {
  const [checked, setChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setChecked(true);
        return;
      }
      
      try {
        await api.get('/auth/me');
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('token');
      }
      setChecked(true);
    };
    
    checkAuth();
  }, []);
  
  if (!checked) {
    return <AdeLoadingScreen />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/app/home" replace />;
  }
  
  return children;
};

function AppRouter() {
  const location = useLocation();
  useRouteRobots(location.pathname);
  return (
    <Suspense fallback={<AdeLoadingScreen />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Email Verification */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      
      {/* Public Legal Pages (no auth required - App Store requirement) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/creator-terms" element={<CreatorTerms />} />
      <Route path="/golden-ring-terms" element={<GoldenRingTerms />} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      
      {/* Protected App Routes */}
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/home" replace />} />
        <Route path="home" element={<Home />} />
        <Route path="here" element={<Here />} />
        <Route path="messages" element={<Messages />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="nearby" element={<Nearby />} />
        <Route path="connections" element={<Connections />} />
        <Route path="requests" element={<Requests />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      
      {/* User Profile */}
      <Route path="/app/user/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />

      {/* Professional Profile (Phase 2) */}
      <Route path="/app/pro/:userId" element={<ProtectedRoute><ProfessionalPublic /></ProtectedRoute>} />
      <Route path="/app/professionals" element={<ProtectedRoute><ProfessionalsSearch /></ProtectedRoute>} />
      <Route path="/app/settings/professional" element={<ProtectedRoute><ProfessionalSettings /></ProtectedRoute>} />
      <Route path="/app/me/following" element={<Navigate to="/app/connections" replace />} />
      <Route path="/app/professional/audience" element={<ProtectedRoute><ProfessionalAudience /></ProtectedRoute>} />
      <Route path="/app/professional/analytics" element={<ProtectedRoute><ProfessionalAnalytics /></ProtectedRoute>} />
      
      {/* Chat */}
      <Route path="/app/chat/:userId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      
      {/* Shop Success/Cancel */}
      <Route path="/app/shop/success" element={<ProtectedRoute><PurchaseSuccessPage /></ProtectedRoute>} />
      <Route path="/app/shop/cancel" element={<Navigate to="/app/home" replace />} />
      
      {/* Creator Program */}
      <Route path="/app/discussion/:id" element={<ProtectedRoute><DiscussionPage /></ProtectedRoute>} />
      <Route path="/app/missions/history" element={<ProtectedRoute><MissionsHistory /></ProtectedRoute>} />
      <Route path="/app/pro/missions" element={<ProtectedRoute><MerchantMissions /></ProtectedRoute>} />
      <Route path="/app/pro/missions/new" element={<ProtectedRoute><MerchantMissionCreate /></ProtectedRoute>} />
      <Route path="/app/missions/active/:assignmentId" element={<ProtectedRoute><ActiveMission /></ProtectedRoute>} />
      <Route path="/app/missions/:campaignId" element={<ProtectedRoute><MissionDetail /></ProtectedRoute>} />
      <Route path="/app/creator-apply" element={<ProtectedRoute><CreatorRegistration /></ProtectedRoute>} />
      <Route path="/app/creator-wallet" element={<ProtectedRoute><CreatorWallet /></ProtectedRoute>} />
      <Route path="/app/creator-dashboard" element={<ProtectedRoute><CreatorDashboardPage /></ProtectedRoute>} />
      <Route path="/app/creator-progress" element={<ProtectedRoute><CreatorProgress /></ProtectedRoute>} />
      
      {/* Economy Pages */}
      <Route path="/app/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
      <Route path="/app/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
      <Route path="/app/calls" element={<ProtectedRoute><CallHistory /></ProtectedRoute>} />
      <Route path="/app/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
      <Route path="/app/explore/:tag" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
      <Route path="/app/mention/:username" element={<ProtectedRoute><MentionRedirect /></ProtectedRoute>} />
      <Route path="/app/gift-center" element={<ProtectedRoute><GiftCenter /></ProtectedRoute>} />
      <Route path="/app/golden-ring" element={<ProtectedRoute><GoldenRingPage /></ProtectedRoute>} />
      <Route path="/app/creator/kyc" element={<ProtectedRoute><CreatorKycPage /></ProtectedRoute>} />
      
      {/* Admin Panel - Separate authentication */}
      <Route path="/admin" element={<AdminPanel />} />
      
      {/* Public SEO Pages (no auth required) */}
      <Route path="/post/:photoId" element={<PublicPost />} />
      <Route path="/:handle" element={<PublicProfile />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="app-container">
        <BrowserRouter>
          <AuthContext>
            <EdgeSwipeBack />
            <AppRouter />
            <CallManager />
          </AuthContext>
        </BrowserRouter>
        <CookieBanner />
        <Toaster position="top-center" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
